import React, { useState, useEffect } from 'react'
import { Fingerprint, X, Shield, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const PasskeyOnboarding: React.FC = () => {
  const navigate = useNavigate()
  const [show, setShow] = useState(false)
  const [passkeyCount, setPasskeyCount] = useState<number | null>(null)

  useEffect(() => {
    let isMounted = true

    const checkPasskeyStatus = async () => {
      try {
        // Check if user has dismissed the prompt before
        const dismissed = localStorage.getItem('passkey-onboarding-dismissed')
        if (dismissed) {
          return
        }

        // Check if WebAuthn is supported
        if (!window.PublicKeyCredential) {
          return
        }

        // Check if user has any passkeys
        const token = localStorage.getItem('auth-token')
        if (!token) {
          return
        }

        const response = await fetch('/api/auth/user/passkeys', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()

          if (isMounted) {
            setPasskeyCount(data.passkeys?.length || 0)

            // Show banner if user has no passkeys
            if (!data.passkeys || data.passkeys.length === 0) {
              setShow(true)
            }
          }
        }
      } catch (error: any) {
        console.error('Failed to check passkey status:', error, {
          message: error?.message,
          stack: error?.stack
        })
      }
    }

    checkPasskeyStatus()
    return () => { isMounted = false }
  }, [])

  const handleDismiss = () => {
    localStorage.setItem('passkey-onboarding-dismissed', 'true')
    setShow(false)
  }

  const handleSetupNow = () => {
    navigate('/profile')
  }

  if (!show || passkeyCount === null) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-md z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-2xl p-6 text-white">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/80 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
              <Fingerprint className="h-7 w-7 text-white" />
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">
              Upgrade Your Security
            </h3>
            <p className="text-sm text-blue-100 mb-4">
              Set up passkey authentication for faster, more secure sign-in with TouchID, FaceID, or Windows Hello.
            </p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-300" />
                <span>No passwords to remember</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-300" />
                <span>Phishing resistant</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Fingerprint className="h-4 w-4 text-green-300" />
                <span>Sign in with biometrics</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSetupNow}
                className="flex-1 bg-white text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Set Up Now
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-white/90 hover:text-white text-sm font-medium"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasskeyOnboarding
